import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Position, DEFAULT_POSITIONS } from '@/lib/types/admin';

const COLLECTION = 'positions';

// Get all positions (for admin)
export const getPositions = async (): Promise<Position[]> => {
  const q = query(collection(db, COLLECTION), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Position[];
};

// Get active positions only (for public apply form)
export const getActivePositions = async (): Promise<{ value: string; label: string }[]> => {
  try {
    // Fetch all positions and filter client-side to avoid index requirement
    const q = query(collection(db, COLLECTION), orderBy('order', 'asc'));
    const snapshot = await getDocs(q);
    const positions = snapshot.docs
      .map((doc) => ({
        value: doc.data().value,
        label: doc.data().label,
        active: doc.data().active,
      }))
      .filter((p) => p.active)
      .map(({ value, label }) => ({ value, label }));

    // Return default positions if none exist in Firestore
    if (positions.length === 0) {
      return DEFAULT_POSITIONS;
    }

    return positions;
  } catch (error) {
    console.error('Error fetching positions:', error);
    return DEFAULT_POSITIONS;
  }
};

// Create position
export const createPosition = async (
  data: Omit<Position, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const docRef = await addDoc(collection(db, COLLECTION), {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
};

// Update position
export const updatePosition = async (
  id: string,
  data: Partial<Omit<Position, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
};

// Delete position
export const deletePosition = async (id: string): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  await deleteDoc(docRef);
};

// Toggle position active status
export const togglePositionActive = async (
  id: string,
  active: boolean
): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  await updateDoc(docRef, {
    active,
    updatedAt: Timestamp.now(),
  });
};

// Reorder positions
export const reorderPositions = async (
  positionIds: string[]
): Promise<void> => {
  const batch = positionIds.map((id, index) => {
    const docRef = doc(db, COLLECTION, id);
    return updateDoc(docRef, { order: index });
  });
  await Promise.all(batch);
};

// Generate slug from label
export const generatePositionValue = (label: string): string => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Initialize default positions in Firestore (one-time setup)
export const initializeDefaultPositions = async (): Promise<void> => {
  const existing = await getPositions();
  if (existing.length > 0) return;

  const promises = DEFAULT_POSITIONS.map((pos, index) =>
    createPosition({
      value: pos.value,
      label: pos.label,
      active: true,
      order: index,
    })
  );
  await Promise.all(promises);
};
